/*
 * File: strict-number.ts
 * File Created: 2024-01-14 03:09:05
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-11-08 02:41:08
 * Modified By: Val Liu
 * -----
 */

import Vue, { VNode } from "vue";
import { DirectiveBinding, DirectiveFunction, DirectiveOptions } from "vue/types/options";
import { StringHelper } from "../utilities/string-helper";
import { NumberHelper } from "../utilities";
import { getBinding, getChildInput } from "./core";

interface IStrictNumber {
    enabled?: boolean;
    decimal?: number;
    value?: number;
    "update:value"?: (value: number) => void;
}

const defaultOptions = {
    enabled: true,
    decimal: 0,
};

const largestPossibleNumber = 100000000000000;

/// last value stored as number
const LAST_VALUE = Symbol(`Last Value`);
const INITIAL = Symbol(`Initial`);
const LISTENER = Symbol(`Input Listener`);
const HANDLE_START = Symbol(`Handle Start`);
const HANDLE_END = Symbol(`Handle End`);
const COMPOSING = Symbol(`Composing`);

Vue.directive("strict-number", {
    componentUpdated(el, binding, vnode) {
        return componentUpdated.apply(this, arguments as any);
    },

    bind(el, binding, vnode) {
        return bind.apply(this, arguments as any);
    },
    unbind(el) {
        const input = getChildInput(el);
        if (!input || !input[LISTENER]) return;

        input.removeEventListener("input", input[LISTENER]);
        input.removeEventListener("compositionstart", input[HANDLE_START]);
        input.removeEventListener("compositionend", input[HANDLE_END]);
        delete input[LISTENER];
        delete input[COMPOSING];
    },
});

function bind(
    this: DirectiveOptions | DirectiveFunction,
    el: HTMLElement,
    binding: DirectiveBinding,
    vnode: VNode,
) {
    let {
        enabled,
        decimal,
        "update:value": update,
    } = getBinding<IStrictNumber>(binding, defaultOptions);

    const input = getChildInput(el);
    if (!input) return;

    const handleCompositionStart = () => {
        input[COMPOSING] = true;
    };

    const handleCompositionEnd = () => {
        input[COMPOSING] = false;
        inputListener();
    };

    const inputListener = (e?: any) => {
        if (input[COMPOSING]) return;

        let adjustPos = 0;
        decimal = decimal ?? defaultOptions.decimal;

        const selectionStart = input.selectionStart ?? 0;
        const selectionEnd = input.selectionEnd ?? 0;
        const oldValue = input.value;
        const oldValueLength = oldValue.length;
        const lastValue = formatNumber(input[LAST_VALUE], decimal) ?? "";

        if (enabled === false) {
            emitUpdateValue(input, update, oldValue);
            return;
        }
        /// format the number
        let formattedValue = formatNumber(oldValue, decimal);
        let numberedValue = StringHelper.toNumber(formattedValue) ?? 0;

        if (
            // prevent adding multiple dots
            (e?.data === "." && lastValue.includes(".")) ||
            // prevent adding multiple -
            (e?.data === "-" && lastValue.includes("-")) ||
            // prevent exceed the maximum number
            numberedValue > largestPossibleNumber ||
            numberedValue < -largestPossibleNumber ||
            /// prevent leading zero, but allow pure zero.
            (e?.data === "0" &&
                formattedValue.length > 1 &&
                lastValue.length === formattedValue.length)
        ) {
            // Prevent adding another dot
            input.value = lastValue;
            input.setSelectionRange(selectionStart - 1, selectionEnd - 1);
            // console.log("restore to previous value");
            return;
        }

        /// if nothing deleted or added
        if (lastValue.length - formattedValue.length === 0) {
            /// check for "," deletion
            if (e?.inputType === "deleteContentBackward") {
                const deleted = lastValue[selectionStart];
                if (deleted === ",") {
                    adjustPos = -1;
                    formattedValue = formatNumber(
                        lastValue.substring(0, selectionStart - 1) +
                            lastValue.substring(selectionStart + 1, lastValue.length),
                        decimal,
                    );
                }
            } else if (e?.inputType === "deleteContentForward") {
                const deleted = lastValue[selectionStart];
                if (deleted === ",") {
                    formattedValue = formatNumber(
                        lastValue.substring(0, selectionStart) +
                            lastValue.substring(selectionStart + 2, lastValue.length),
                        decimal,
                    );
                }
            }
        }

        if (formattedValue !== oldValue || adjustPos) {
            // Store the number of characters before the selection start
            let charsBefore = oldValue
                .substring(0, selectionStart)
                .replace(/\D/g, "").length;
            // Adjust charsBefore for comma deletion
            if (adjustPos) {
                charsBefore += adjustPos;
            }

            input.value = formattedValue;

            // Find the new cursor position
            let newPosition = 0;
            let charCount = 0;
            for (let i = 0; i < formattedValue.length && charCount < charsBefore; i++) {
                if (!isNaN(parseInt(formattedValue[i]))) {
                    charCount++;
                }
                newPosition++;
            }

            // Restore cursor position
            input.setSelectionRange(newPosition, newPosition);

            //     /// call update
            //     emitUpdateValue(input, update, numberedValue);
            // } else {
            //     /// should always call update on keystroke
            //     emitUpdateValue(input, update, numberedValue);
        }

        /// should always call update on keystroke
        // emit "undefined" if input.value is "" for the second time.
        const emitValue =
            lastValue === "0" && formattedValue === "" ? undefined : numberedValue;
        emitUpdateValue(input, update, emitValue);
    };
    input[LISTENER] = inputListener;
    input.addEventListener("input", inputListener);
    input[HANDLE_START] = handleCompositionStart;
    input.addEventListener("compositionstart", handleCompositionStart);
    input[HANDLE_END] = handleCompositionEnd;
    input.addEventListener("compositionend", handleCompositionEnd);

    /// initial binding
    input[INITIAL] = true;
    componentUpdated.apply(this as any, arguments as any);
}

function componentUpdated(
    this: DirectiveOptions | DirectiveFunction,
    el: HTMLElement,
    binding: DirectiveBinding,
    vnode: VNode,
) {
    const {
        enabled,
        decimal,
        "update:value": update,
    } = getBinding<IStrictNumber>(binding, defaultOptions);

    const input = getChildInput(el);
    if (input == null) return;

    const value: number = binding.value?.value;
    const oldValue: number = binding.oldValue?.value;
    const formattedValue = enabled
        ? formatNumber(value, decimal ?? defaultOptions.decimal)
        : value;
    /// if the formatted value is exactly the same as input value, return.
    if (input.value === formattedValue || isNaN(value)) return;

    const execute = (input) => {
        input.value = formattedValue;
        emitUpdateValue(input, update, value);
    };
    /// if initialed, add a short delay.
    let init = input[INITIAL];
    if (init) {
        input[INITIAL] = false;
        setTimeout(() => execute(input), 0);
    } else {
        execute(input);
    }
}

function formatNumber(value: string | number | undefined, decimal: number): string {
    if (value == undefined) return "";
    let isNegative = false;
    let o = typeof value === "number" ? String(value) : value;

    // Check if the number is negative
    if (o.startsWith("-")) {
        isNegative = true;
        o = o.substring(1); // Remove the negative sign for formatting
    }

    /// remove leading zero if not pure zero
    if (!/^0(\.(\d+)?)?$/.test(o)) o = o.replace(/^[0]+/, "");

    // Check for trailing dot
    let hasTrailingDot = o.endsWith(".") && decimal !== 0;

    // First, separate the number into the whole part and the decimal part
    let [whole, decimalPart] = o.split(".");

    // Format the whole part with commas
    whole = whole.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // If a decimal part exists and a limit is set, truncate it to the specified number of decimal places
    if (decimalPart && decimal !== undefined) {
        decimalPart = decimalPart.replace(/,/g, "");
        decimalPart = decimalPart.substring(0, decimal);
    }

    // Reassemble the whole number and decimal part
    let formattedNumber = hasTrailingDot
        ? `${whole}.`
        : decimalPart
          ? `${whole}.${decimalPart}`
          : whole;

    // Re-add the negative sign if the number was negative
    return isNegative ? `-${formattedNumber}` : formattedNumber;
}

function emitUpdateValue(
    input: HTMLInputElement,
    update: Function | undefined,
    value: string | number | undefined,
) {
    if (typeof value === "string") {
        value = StringHelper.toNumber(value) ?? undefined;
    }
    input[LAST_VALUE] = value;
    if (update && typeof update === "function") {
        update(value);
    }
}
